import type { AdminQueueCountsDto } from '@carcheck/shared';
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

import { useAdminQueueCounts } from '../../api';

/**
 * Per-route queue counts displayed as small badges on sidebar nav rows.
 *
 * Fed by `GET /admin/queue-counts` (AUT-103) via `useAdminQueueCounts`.
 * SINGLE FETCH, TWO CONSUMERS: this provider feeds the sidebar badges
 * while the dashboard count tiles call the same hook directly — TanStack
 * dedupes by key so both share one network request.
 *
 * Label mapping (reconciled against `AdminQueueCountsDto`):
 *   - `pendingReports`  → Отчёты   badge. **The field name is a leftover.**
 *     AUT-265 repointed the server-side predicate from "reports awaiting a
 *     moderator" (there is no moderator) to STUCK reports: `submitted` +
 *     `acceptedAt IS NULL` + `finalizedAt < now-24h`, i.e. the auto-accept
 *     job died or an appeal is holding it. The badge's hover title says so.
 *     (`AdminQueueCountsDto`'s own JSDoc in `@carcheck/shared` still
 *     documents the old predicate — cross-package, tracked separately.)
 *   - `pendingAppeals`  → Аппеляции badge
 *   - `pendingKyc`      → Инспекторы badge (KYC awaiting manual review)
 *   - `contentReportsOpen` → Жалобы badge («Площадка», AUT-542) — open
 *     complaints. The only count in that section: «Специалисты»,
 *     «Объявления», «Заявки» have none.
 *   - Orders badge      → NO queue-count source; left unset (hidden). We
 *     do NOT fake it — the DTO carries no order-queue count.
 *
 * Renderers read counts via `useQueueCounts()`; `SidebarItem` renders the
 * badge slot only for a positive number (LOCK 13 — 0 / undefined hides).
 *
 * A `counts` override prop is retained for tests / storybook; when
 * supplied it wins over the fetched value.
 */
export type QueueCounts = Partial<{
  orders: number;
  reports: number;
  appeals: number;
  inspectors: number;
  contentReports: number;
}>;

/** Map the wire DTO to the sidebar badge shape (Orders deliberately omitted). */
const toBadgeCounts = (dto: AdminQueueCountsDto | undefined): QueueCounts => {
  if (!dto) return {};
  return {
    reports: dto.pendingReports,
    appeals: dto.pendingAppeals,
    inspectors: dto.pendingKyc,
    contentReports: dto.contentReportsOpen,
  };
};

const QueueCountsContext = createContext<QueueCounts>({});

export const QueueCountsProvider = ({
  children,
  counts,
}: {
  children: ReactNode;
  counts?: QueueCounts;
}): ReactElement => {
  const { data } = useAdminQueueCounts();
  const value = counts ?? toBadgeCounts(data);
  return <QueueCountsContext.Provider value={value}>{children}</QueueCountsContext.Provider>;
};

export const useQueueCounts = (): QueueCounts => useContext(QueueCountsContext);
